import type { NavigationFailure, RouteLocationRaw, Router } from 'vue-router'
import { type ReleasedUrlsChecker } from './released-urls'
import { getWindow } from '@/helpers/globals'
import { type App, inject } from 'vue'

export interface RouterNavigationPlugin {
    routerPush: (
        to: RouteLocationRaw
    ) => Promise<void | NavigationFailure | undefined>
    routerReplace: (
        to: RouteLocationRaw
    ) => Promise<void | NavigationFailure | undefined>
}

const key = 'app.plugin.router-navigation'
export function installRouterNavigation(
    app: App,
    router: Router,
    isReleasedUrl: ReleasedUrlsChecker
): RouterNavigationPlugin {
    const navigation = createRouterNavigation(router, isReleasedUrl)

    app.provide(key, navigation)

    return navigation
}

function createRouterNavigation(
    router: Router,
    isReleasedUrl: ReleasedUrlsChecker
): RouterNavigationPlugin {
    const location = getWindow()?.location

    const routerPush = (
        to: RouteLocationRaw
    ): Promise<void | NavigationFailure | undefined> => {
        const route = router.resolve(to)

        return isReleasedUrl(route)
            ? router.push(to)
            : Promise.resolve(location?.assign(route.fullPath))
    }

    const routerReplace = (
        to: RouteLocationRaw
    ): Promise<void | NavigationFailure | undefined> => {
        const route = router.resolve(to)

        return isReleasedUrl(route)
            ? router.replace(to)
            : Promise.resolve(location?.replace(route.fullPath))
    }

    return {
        routerPush,
        routerReplace,
    }
}

export function useRouterNavigation(): RouterNavigationPlugin {
    return inject(key) as RouterNavigationPlugin
}
